import { useReducer, useEffect, useCallback } from 'react'
import { debounce } from 'lodash'
import { KeyValueFilter, ValueFilter } from '@api/employee/types'
import { Toaster } from '@ui'
import { Filters } from '../../EmployeeManagement/hooks/useEmployees'
import { IVariables } from '@api/employee/catalog'
import api, { ICatalogEmployee, IPageInfo } from '@api'
import { get } from 'lodash'
import { ApolloError } from 'apollo-boost'
import { useShouldReload } from '@hooks/reloadContext'

enum Actions {
  SEARCH,
  LOAD_SUCCESS,
  LOAD_START,
  FILTER,
  NEXT_PAGE,
}

export type EmployeeCatalogFiltersT = Omit<Filters, 'filters'> & {
  filters: Omit<Filters['filters'], 'geoLocation'>
}

interface State {
  isLoading: boolean
  query: string
  after: string | null
  isPaging: boolean | null
  pageInfo: IPageInfo
  items: ICatalogEmployee[]
  reqId: number
  filters: EmployeeCatalogFiltersT
  count: number
}

export interface EmployeeCatalog extends State {
  search: (query: string) => void
  applyFilters: (filters: EmployeeCatalogFiltersT) => void
  next: () => void
  clearFilters: () => void
}

interface SearchAction {
  type: Actions.SEARCH
  query: string
  reqId: number
}

interface LoadSuccessAction {
  type: Actions.LOAD_SUCCESS
  items: ICatalogEmployee[]
  pageInfo: IPageInfo
  count: number
  reqId: number
}

interface LoadStartAction {
  type: Actions.LOAD_START
}

interface FilterAction {
  type: Actions.FILTER
  filters: EmployeeCatalogFiltersT
}

interface LoadNextPageAction {
  type: Actions.NEXT_PAGE
}

type Action = LoadSuccessAction | SearchAction | FilterAction | LoadNextPageAction | LoadStartAction

function reducer(state: State, action: Action) {
  switch (action.type) {
    case Actions.SEARCH:
      return {
        ...state,
        after: null,
        isLoading: true,
        items: [],
        reqId: action.reqId,
        query: action.query,
      }

    case Actions.LOAD_SUCCESS:
      return state.reqId > action.reqId
        ? state
        : {
            ...state,
            items: state.isPaging ? [...state.items, ...action.items] : action.items,
            isPaging: false,
            pageInfo: action.pageInfo,
            isLoading: false,
            count: action.count,
          }

    case Actions.LOAD_START:
      return {
        ...state,
        isLoading: true,
      }

    case Actions.NEXT_PAGE:
      return {
        ...state,
        after: state.pageInfo.endCursor,
        isPaging: true,
      }

    case Actions.FILTER:
      return {
        ...state,
        items: [],
        filters: action.filters,
        after: null,
        query: '',
        isLoading: true,
        pageInfo: { hasNextPage: false, endCursor: '' },
      }

    default:
      return state
  }
}

export function getVariables({
  first,
  after,
  filters: filtersState,
}: {
  first?: number
  after?: string | null
  filters: EmployeeCatalogFiltersT
}): {
  first: number
  after?: string | null
  ValueFilter: ValueFilter[]
  keyValueFilters: KeyValueFilter[]
  filters: KeyValueFilter[]
} {
  let keyValueFilters: KeyValueFilter[] = []
  if (filtersState.keyValueFilters.department) {
    keyValueFilters = [
      {
        key: 'DepartmentId',
        value: filtersState.keyValueFilters.department.id,
      },
    ]
  }

  const filters: KeyValueFilter[] = []
  const managerId = get(filtersState, 'filters.manager.id')
  const floorId = get(filtersState, 'filters.floor.id')
  const type = filtersState.filters.type
  const role = filtersState.filters.role
  const query = filtersState.filters.query

  managerId && filters.push({ key: 'managerId', value: managerId })
  floorId && filters.push({ key: 'floorId', value: floorId })
  type && filters.push({ key: 'type', value: type })
  role && filters.push({ key: 'role', value: role })
  query && filters.push({ key: 'query', value: query })

  return {
    after,
    first: first || 25,
    ValueFilter: filtersState.valueFilter,
    keyValueFilters,
    filters,
  }
}

let persistedFilterableState: State = {
  reqId: 0,
  isLoading: true,
  after: '',
  isPaging: false,
  query: '',
  items: [],
  pageInfo: { hasNextPage: false, endCursor: '' },
  count: 0,
  filters: {
    cities: [],
    valueFilter: [],
    keyValueFilters: {
      department: null,
    },
    filters: {
      manager: null,
      floor: null,
      building: null,
      type: null,
      role: null,
      query: '',
      title: null,
    },
  },
}

let persistedState: State = {
  reqId: 0,
  isLoading: false,
  after: '',
  isPaging: false,
  query: '',
  items: [],
  pageInfo: { hasNextPage: false, endCursor: '' },
  count: 0,
  filters: {
    cities: [],
    valueFilter: [],
    keyValueFilters: {
      department: null,
    },
    filters: {
      manager: null,
      floor: null,
      building: null,
      type: null,
      role: null,
      query: '',
      title: null,
    },
  },
}

export default function useEmployeeCatalog(
  assignmentPlanId?: string,
  filterable?: boolean
): EmployeeCatalog {
  const shouldReload = useShouldReload()
  const [state, dispatch] = useReducer(
    reducer,
    filterable ? persistedFilterableState : persistedState
  )

  const debouncedSearch = useCallback(
    debounce((variables: IVariables, reqId: number) => {
      dispatch({ type: Actions.LOAD_START })

      api.employee
        .catalog(variables as IVariables)
        .then(({ edges, counters, pageInfo }) => {
          const items = edges.map(({ node }) => node)

          dispatch({
            type: Actions.LOAD_SUCCESS,
            reqId,
            items,
            pageInfo,
            count: counters.total,
          })
        })
        .catch((error: ApolloError) => Toaster.fail({ error }))
    }, 300),
    []
  )

  useEffect(() => {
    const reqId = Date.now()

    const variables = getVariables({
      after: state.after,
      filters: {
        ...state.filters,
        filters: { ...state.filters.filters, query: state.query },
      },
    })

    debouncedSearch({ ...variables, assignmentPlanId } as IVariables, reqId)

    return () => debouncedSearch.cancel()
  }, [
    assignmentPlanId,
    state.filters,
    state.after,
    state.query,
    debouncedSearch,
    shouldReload.employeeCatalog,
  ])

  useEffect(() => {
    if (filterable) {
      persistedFilterableState = { ...state, after: null }
    } else {
      persistedState = { ...state, after: null }
    }
  })

  return {
    ...state,
    search: (query: string) =>
      dispatch({
        type: Actions.SEARCH,
        query,
        reqId: Date.now(),
      }),
    next: () => {
      if (state.pageInfo.hasNextPage) dispatch({ type: Actions.NEXT_PAGE })
    },
    clearFilters: () => {
      dispatch({
        type: Actions.FILTER,
        filters: {
          cities: [],
          valueFilter: [],
          keyValueFilters: {
            department: null,
          },
          filters: {
            manager: null,
            floor: null,
            building: null,
            type: null,
            role: null,
            query: '',
            title: null,
          },
        },
      })
    },
    applyFilters: (filters) => {
      dispatch({ type: Actions.FILTER, filters })
    },
  }
}
